import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import LanguageSwitcher from './common/LanguageSwitcher';
import './LandingPage.css';
import './SignupPage.css';
import './PartnersPage.css';

const PartnersApplyPage = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const typeFromQuery = (searchParams.get('type') || '').toLowerCase();
  const initialPartnerType = ['teacher', 'school', 'influencer'].includes(typeFromQuery)
    ? typeFromQuery
    : 'teacher';
  const [logoUrl] = useState(null);
  const [accountMode, setAccountMode] = useState('signup'); // 'signup' | 'login'
  const [accountStepDone, setAccountStepDone] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [applicant, setApplicant] = useState({ name: '', email: '', id: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [accountForm, setAccountForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
  });
  const [partnerForm, setPartnerForm] = useState({
    partnerType: initialPartnerType,
    schoolOrSocial: '',
    schoolEmail: '',
    audienceSize: '',
    message: '',
  });
  const [schoolAccount, setSchoolAccount] = useState({
    contactName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [businessDoc, setBusinessDoc] = useState(null);
  const [businessDocError, setBusinessDocError] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const handlePartnerInputChange = (e) => {
    const { name, value } = e.target;
    // For direct School flow, lock partnerType to 'school' (no choices)
    if (partnerForm.partnerType === 'school' && name === 'partnerType') return;
    setPartnerForm((prev) => ({ ...prev, [name]: value }));
    setSubmitStatus(null);
  };

  const storeAuth = (data) => {
    if (data?.token) localStorage.setItem('token', data.token);
    if (data?.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    if (data?.user) localStorage.setItem('user', JSON.stringify(data.user));
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setAccountError('');
    setAccountLoading(true);

    try {
      const API_BASE_URL = getApiBaseUrl();

      if (accountMode === 'signup') {
        if (!accountForm.name.trim()) throw new Error('Please enter your full name');
        if (!accountForm.email.trim()) throw new Error('Please enter your email');
        if (!accountForm.password) throw new Error('Please enter a password');
        if (accountForm.password.length < 8) throw new Error('Password must be at least 8 characters');
        if (accountForm.password !== accountForm.confirmPassword) throw new Error('Passwords do not match');

        const signupRes = await fetch(`${API_BASE_URL}/api/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: accountForm.name.trim(),
            email: accountForm.email.trim(),
            password: accountForm.password,
            role: 'student',
          }),
        });

        const signupData = await signupRes.json().catch(() => ({}));
        if (!signupRes.ok || !signupData.success) {
          throw new Error(signupData.message || signupData.error || 'Signup failed. Please try again.');
        }

        // Auto-login after signup
        const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: (accountForm.email || '').trim().toLowerCase(), password: accountForm.password }),
        });
        const loginData = await loginRes.json().catch(() => ({}));
        if (!loginRes.ok || !loginData.success) {
          throw new Error(loginData.message || 'Account created, but login failed. Please try logging in.');
        }

        storeAuth(loginData);
        setApplicant({
          name: loginData?.user?.name || accountForm.name.trim(),
          email: loginData?.user?.email || accountForm.email.trim(),
          id: loginData?.user?.id ?? null,
        });
      } else {
        if (!loginForm.email.trim()) throw new Error('Please enter your email');
        if (!loginForm.password) throw new Error('Please enter your password');

        const loginRes = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: (loginForm.email || '').trim().toLowerCase(), password: loginForm.password }),
        });
        const loginData = await loginRes.json().catch(() => ({}));
        if (!loginRes.ok || !loginData.success) {
          throw new Error(loginData.message || 'Invalid email or password');
        }

        storeAuth(loginData);
        setApplicant({
          name: loginData?.user?.name || '',
          email: loginData?.user?.email || loginForm.email.trim(),
          id: loginData?.user?.id ?? null,
        });
      }

      setAccountStepDone(true);
    } catch (err) {
      console.error('Account setup error:', err);
      setAccountError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const API_BASE_URL = getApiBaseUrl();
      const formData = new FormData();
      if (partnerForm.partnerType === 'school') {
        if (!schoolAccount.contactName.trim()) throw new Error(t('partnersApply.schoolContactNameRequired', 'Please enter a contact name'));
        if (!schoolAccount.email.trim()) throw new Error(t('partnersApply.schoolEmailRequired', 'Please enter the official school email'));
        if (!schoolAccount.password) throw new Error(t('partnersApply.schoolPasswordRequired', 'Please enter a password'));
        if (schoolAccount.password.length < 8) throw new Error(t('partnersApply.passwordMin8', 'Password (min. 8 characters)'));
        if (schoolAccount.password !== schoolAccount.confirmPassword) throw new Error(t('partnersApply.confirmPassword', 'Confirm Password'));

        formData.append('name', schoolAccount.contactName.trim());
        formData.append('email', schoolAccount.email.trim().toLowerCase());
        formData.append('schoolEmail', schoolAccount.email.trim().toLowerCase());
        formData.append('schoolPassword', schoolAccount.password);
      } else {
        formData.append('name', applicant.name || '');
        formData.append('email', applicant.email || '');
        if (applicant.id != null) {
          formData.append('userId', String(applicant.id));
        }
      }
      formData.append('partnerType', partnerForm.partnerType);
      formData.append('schoolOrSocial', partnerForm.schoolOrSocial);
      formData.append('audienceSize', partnerForm.audienceSize);
      formData.append('message', partnerForm.message || '');
      formData.append('agreeTerms', agreeTerms ? 'true' : 'false');
      if (partnerForm.partnerType === 'school' && businessDoc) {
        formData.append('businessDoc', businessDoc);
      }

      const res = await fetch(`${API_BASE_URL}/api/partner-requests`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to submit application');
      setSubmitStatus('success');
      setPartnerForm({
        partnerType: 'teacher',
        schoolOrSocial: '',
        schoolEmail: '',
        audienceSize: '',
        message: '',
      });
      setSchoolAccount({ contactName: '', email: '', password: '', confirmPassword: '' });
      setBusinessDoc(null);
      setBusinessDocError('');
      setAgreeTerms(false);
    } catch (err) {
      console.error('Partner application error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page business-page">
      <nav className="landing-navbar">
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            {logoUrl ? (
              <img src={getImageUrl(logoUrl) || logoUrl} alt="Syllera" className="landing-logo-img" />
            ) : (
              <span className="material-icons">school</span>
            )}
            <span>Syllera</span>
          </div>
          <div className="nav-menu">
            <a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>{t('nav.features')}</a>
            <a href="/#how-it-works" onClick={(e) => { e.preventDefault(); navigate('/#how-it-works'); }}>{t('nav.howItWorks')}</a>
            <a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>{t('nav.pricing')}</a>
            <a href="/#testimonials" onClick={(e) => { e.preventDefault(); navigate('/#testimonials'); }}>{t('nav.reviews')}</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); }}>{t('nav.resources')}</a>
            <a href="/business" onClick={(e) => { e.preventDefault(); navigate('/business'); }}>{t('nav.business')}</a>
            <a href="/partners" onClick={(e) => { e.preventDefault(); navigate('/partners'); }}>{t('nav.partners')}</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => navigate('/#login')}>{t('nav.login')}</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>{t('nav.startLearning')}</button>
          </div>
        </div>
      </nav>

      <section className="signup-section partners-apply-page-section">
        <div className="signup-container">
          <div className="signup-box">
            <a href="/partners" onClick={(e) => { e.preventDefault(); navigate('/partners'); }} className="partners-apply-back">
              <span className="material-icons">arrow_back</span> {t('partnersApply.backToPartners')}
            </a>
            {!accountStepDone && partnerForm.partnerType !== 'school' ? (
              <>
                <div className="signup-header">
                  <div className="signup-icon">
                    <span className="material-icons">person_add</span>
                  </div>
                  <h2>{t('partnersApply.accountSetup')}</h2>
                  <p className="signup-subtitle">{t('partnersApply.accountSetupSubtitle')}</p>
                </div>

                <div className="partners-apply-mode-tabs" role="tablist" aria-label="Account setup mode">
                  <button
                    type="button"
                    className={`partners-apply-mode-btn ${accountMode === 'signup' ? 'active' : ''}`}
                    onClick={() => { setAccountMode('signup'); setAccountError(''); }}
                  >
                    {t('partnersApply.createAccount')}
                  </button>
                  <button
                    type="button"
                    className={`partners-apply-mode-btn ${accountMode === 'login' ? 'active' : ''}`}
                    onClick={() => { setAccountMode('login'); setAccountError(''); }}
                  >
                    {t('partnersApply.alreadyHaveAccount')}
                  </button>
                </div>

                <form onSubmit={handleAccountSubmit} className="signup-form">
                  {accountError && (
                    <div className="error-message">
                      <span className="material-icons">error</span>
                      {accountError}
                    </div>
                  )}

                  {accountMode === 'signup' ? (
                    <>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">person</span>
                          <input
                            type="text"
                            name="name"
                            value={accountForm.name}
                            onChange={(e) => setAccountForm((p) => ({ ...p, name: e.target.value }))}
                            placeholder={t('partnersApply.fullName')}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">email</span>
                          <input
                            type="email"
                            name="email"
                            value={accountForm.email}
                            onChange={(e) => setAccountForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder={t('partnersApply.emailAddress')}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">lock</span>
                          <input
                            type="password"
                            name="password"
                            value={accountForm.password}
                            onChange={(e) => setAccountForm((p) => ({ ...p, password: e.target.value }))}
                            placeholder={t('partnersApply.passwordMin8')}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">lock_outline</span>
                          <input
                            type="password"
                            name="confirmPassword"
                            value={accountForm.confirmPassword}
                            onChange={(e) => setAccountForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                            placeholder={t('partnersApply.confirmPassword')}
                            required
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">email</span>
                          <input
                            type="email"
                            name="email"
                            value={loginForm.email}
                            onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))}
                            placeholder={t('partnersApply.emailAddress')}
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <div className="input-group">
                          <span className="material-icons">lock</span>
                          <input
                            type="password"
                            name="password"
                            value={loginForm.password}
                            onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
                            placeholder={t('partnersApply.password')}
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <button className="btn-primary full-width" type="submit" disabled={accountLoading}>
                    {accountLoading ? (
                      <>
                        <span className="material-icons">hourglass_empty</span>
                        {t('partnersApply.continuing')}
                      </>
                    ) : (
                      <>
                        <span className="material-icons">arrow_forward</span>
                        {t('partnersApply.continue')}
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div className="signup-header">
                  <div className="signup-icon">
                    <span className="material-icons">handshake</span>
                  </div>
                  <h2>{t('partnersApply.partnerApplication')}</h2>
                  <p className="signup-subtitle">{t('partnersApply.partnerApplicationSubtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="signup-form">
                {submitStatus === 'success' && (
                  <div className="success-message">
                    <span className="material-icons">check_circle</span>
                    {t('partnersApply.successMessage')}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="error-message">
                    <span className="material-icons">error</span>
                    {t('partnersApply.errorMessage')}
                  </div>
                )}

                {partnerForm.partnerType !== 'school' ? (
                  <>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">person</span>
                        <input type="text" value={applicant.name || ' '} readOnly aria-label={t('partnersApply.fullName')} />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">email</span>
                        <input type="email" value={applicant.email || ' '} readOnly aria-label={t('partnersApply.emailAddress')} />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">person</span>
                        <input
                          type="text"
                          value={schoolAccount.contactName}
                          onChange={(e) => setSchoolAccount((p) => ({ ...p, contactName: e.target.value }))}
                          placeholder={t('partnersApply.schoolContactName', 'School contact name')}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">mail</span>
                        <input
                          type="email"
                          value={schoolAccount.email}
                          onChange={(e) => setSchoolAccount((p) => ({ ...p, email: e.target.value }))}
                          placeholder={t('partnersApply.schoolEmailPlaceholder')}
                          required
                        />
                      </div>
                      <p className="form-hint" style={{ marginTop: 4, fontSize: '0.85rem', color: 'var(--text-muted, #666)' }}>
                        {t('partnersApply.schoolEmailHint')}
                      </p>
                    </div>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">lock</span>
                        <input
                          type="password"
                          value={schoolAccount.password}
                          onChange={(e) => setSchoolAccount((p) => ({ ...p, password: e.target.value }))}
                          placeholder={t('partnersApply.schoolPassword', 'School password (min. 8 characters)')}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <div className="input-group">
                        <span className="material-icons">lock_outline</span>
                        <input
                          type="password"
                          value={schoolAccount.confirmPassword}
                          onChange={(e) => setSchoolAccount((p) => ({ ...p, confirmPassword: e.target.value }))}
                          placeholder={t('partnersApply.confirmPassword')}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                {partnerForm.partnerType === 'school' ? (
                  <div className="form-group">
                    <div className="input-group">
                      <span className="material-icons">business_center</span>
                      <input type="text" value={t('partnersApply.school')} readOnly aria-label={t('partnersApply.school')} />
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="input-group select-group">
                      <span className="material-icons">business_center</span>
                      <select
                        id="partnerType"
                        name="partnerType"
                        value={partnerForm.partnerType}
                        onChange={handlePartnerInputChange}
                        required
                        className="role-select"
                      >
                        <option value="teacher">{t('partnersApply.teacher')}</option>
                        <option value="school">{t('partnersApply.school')}</option>
                        <option value="influencer">{t('partnersApply.influencer')}</option>
                      </select>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <div className="input-group">
                    <span className="material-icons">link</span>
                    <input
                      type="text"
                      id="schoolOrSocial"
                      name="schoolOrSocial"
                      value={partnerForm.schoolOrSocial}
                      onChange={handlePartnerInputChange}
                      required
                      placeholder={partnerForm.partnerType === 'school' ? t('partnersApply.schoolName', 'School name') : t('partnersApply.schoolNameOrHandle')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className="input-group">
                    <span className="material-icons">groups</span>
                    <input
                      type="text"
                      id="audienceSize"
                      name="audienceSize"
                      value={partnerForm.audienceSize}
                      onChange={handlePartnerInputChange}
                      required
                      placeholder={t('partnersApply.audienceSizePlaceholder')}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <div className="input-group input-group-textarea">
                    <span className="material-icons">message</span>
                    <textarea
                      id="message"
                      name="message"
                      value={partnerForm.message}
                      onChange={handlePartnerInputChange}
                      rows="4"
                      placeholder={t('partnersApply.messagePlaceholder')}
                    />
                  </div>
                </div>
                {partnerForm.partnerType === 'school' && (
                  <div className="form-group">
                    <label className="input-label" htmlFor="businessDoc">
                      {t('partnersApply.registerDeCommerce')}
                    </label>
                    <input
                      type="file"
                      id="businessDoc"
                      name="businessDoc"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        setBusinessDocError('');
                        if (!file) {
                          setBusinessDoc(null);
                          return;
                        }
                        if (file.size > 50 * 1024 * 1024) {
                          setBusinessDocError(t('partnersApply.fileTooLarge', 'File is too large.'));
                          setBusinessDoc(null);
                          return;
                        }
                        setBusinessDoc(file);
                      }}
                      required
                    />
                    {businessDocError && (
                      <div className="error-message" style={{ marginTop: 8 }}>
                        <span className="material-icons">error</span>
                        {businessDocError}
                      </div>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      required
                    />
                    <span>
                      {t('partnersApply.agreeToPartnerAgreement')}{' '}
                      <a href="/partners/partner-agreement" target="_blank" rel="noreferrer">
                        {t('partnersApply.partnerAgreement')}
                      </a>
                      .
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn-primary full-width"
                  disabled={(partnerForm.partnerType !== 'school' && !accountStepDone) || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="material-icons">hourglass_empty</span>
                      {t('partnersApply.submitting')}
                    </>
                  ) : (
                    <>
                      <span className="material-icons">person_add</span>
                      {t('partnersApply.applyNow')}
                    </>
                  )}
                </button>
                <p className="signup-terms">
                  {t('partnersApply.byApplyingTerms')}
                </p>
              </form>
              </>
            )}
          </div>
          <div className="signup-benefits">
            <h3>{t('partnersApply.whyPartner')}</h3>
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="material-icons">verified</span>
                <div>
                  <strong>{t('partnersApply.cefrAligned')}</strong>
                  <p>{t('partnersApply.cefrAlignedDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">school</span>
                <div>
                  <strong>{t('partnersApply.educationalLevels')}</strong>
                  <p>{t('partnersApply.educationalLevelsDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">trending_up</span>
                <div>
                  <strong>{t('partnersApply.progressTracking')}</strong>
                  <p>{t('partnersApply.progressTrackingDesc')}</p>
                </div>
              </div>
              <div className="benefit-item">
                <span className="material-icons">payments</span>
                <div>
                  <strong>{t('partnersApply.earnReferrals')}</strong>
                  <p>{t('partnersApply.earnReferralsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnersApplyPage;
